/**
 * Native full-screen modal wrapper for repeatable instance editor (#1154).
 * Web uses RepeatableInstanceEditScreen layout="inline" instead.
 */

import React from 'react';
import { Modal, View, StyleSheet, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import {
  RepeatableInstanceEditScreen,
  type RepeatableInstanceEditScreenProps,
} from './RepeatableInstanceEditScreen';
import { colors } from '../../theme';

export interface RepeatableInstanceEditModalProps
  extends RepeatableInstanceEditScreenProps {
  visible: boolean;
}

function wrapUnitEditorKeyboardProvider(
  body: React.ReactNode,
): React.ReactNode {
  if (Platform.OS === 'web') {
    return body;
  }
  try {
    const { KeyboardProvider } =
      require('react-native-keyboard-controller') as {
        KeyboardProvider: React.ComponentType<{ children: React.ReactNode }>;
      };
    return <KeyboardProvider>{body}</KeyboardProvider>;
  } catch {
    return body;
  }
}

export const RepeatableInstanceEditModal: React.FC<
  RepeatableInstanceEditModalProps
> = ({ visible, ...screenProps }) => {
  const insets = useSafeAreaInsets();

  if (Platform.OS === 'web') {
    return null;
  }

  const body = (
    <View style={[styles.root, { paddingTop: insets.top }]}>
      <RepeatableInstanceEditScreen {...screenProps} layout="modal" />
    </View>
  );

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={screenProps.onClose}
    >
      {wrapUnitEditorKeyboardProvider(body)}
    </Modal>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.backgroundPrimary,
  },
});
